import {gql} from '@apollo/client';
import {useEffect} from 'react';
import {useLazyQuery$} from '../../../graphqlsupport';
import {useObservable} from '../../../rxsupport';

/** One Salesforce picklist entry: the value that is stored, and the text a user reads. */
export type BusinessFunctionOption = {
    label: string;
    value: string;
};

/**
 * One shared empty array rather than a fresh one per render. DropdownMultiSelect re-seeds the boxes
 * a user has ticked whenever its options prop changes identity, so a new array each render throws
 * their choices away.
 */
const NO_OPTIONS: BusinessFunctionOption[] = [];

export const BUSINESS_FUNCTION_OPTIONS_QUERY = gql`
    query businessContactFunctionOptions {
        businessContactFunctionOptions {
            label
            value
        }
    }
`;

export type BusinessFunctionOptions = {
    options: BusinessFunctionOption[];
    /** True until the first answer lands. The form shows the wait rather than a locked picker. */
    loading: boolean;
};

/**
 * The business function picker's options, read from Salesforce through the backend (BHPNPP-3242).
 * The same list is what the save path filters against, so what the picker offers is exactly what a
 * save keeps.
 *
 * <p>Answers an empty list while the query is in flight and when Salesforce cannot be reached. The
 * caller has to read that as "the list is unknown", never as "this contact has no business
 * functions": the picker can only write back what it can see, so a form that trusted it would clear
 * every function a contact carries. ContactForm holds the picker shut and resends the stored value
 * while the list is empty, and tells the user which of the two it is.
 */
export const useBusinessFunctionOptions = (): BusinessFunctionOptions => {
    const {execute, data, isRunning$} = useLazyQuery$<
        Record<string, never>,
        {businessContactFunctionOptions: BusinessFunctionOption[]}
    >(BUSINESS_FUNCTION_OPTIONS_QUERY, {});

    useEffect(() => {
        execute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loading = useObservable(isRunning$, true);

    return {options: data?.businessContactFunctionOptions ?? NO_OPTIONS, loading};
};
